import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAccountOverview } from '../context/AccountOverviewContext';
import { fetchActiveClients, selectActiveClients, selectUtilityLoading } from '../redux/utilitySlice';
import { selectUser, selectClientScope } from '../redux/authSlice';
import { isExtraPage } from '../utils/extraPages';
export const useClientSelection = (moduleName, options = {}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const clients = useSelector(selectActiveClients);
    const cache = useSelector((state) => state.utility.cache);
    const utilityLoading = useSelector(selectUtilityLoading);
    const user = useSelector(selectUser);
    const clientScope = useSelector((state) => selectClientScope(state, moduleName));
    const { selectedClientId: contextClientId, isArchiveMode, setIsArchiveMode } = useAccountOverview();
    // const { isGroupVisible = true } = options 
    const clientList = useMemo(() => {
        const allClients = Array.isArray(clients)
            ? clients
            : (clients?.data || []);
        let filtered = allClients;
        // if (clientScope === 'assigned' && entityType !== 'client') {
        //     const assignedIds = user?.employee_portfolio?.map((p: any) => p._id || p) || [];
        //     filtered = allClients.filter((c: any) => {
        //         const isParentAssigned = assignedIds.includes(c._id);
        //         const hasAssignedChild = c.group_accounts?.some((ga: any) => assignedIds.includes(ga._id));
        //         return isParentAssigned || hasAssignedChild;
        //     });
        // }
        if (options.filterKlaviyo) {
            filtered = filtered.filter((c) => c.connected_networks?.klaviyo === true);
        }
        if (options.filterByType) {
            filtered = filtered.filter((c) => {
                const types = Array.isArray(c.type) ? c.type : (c.type ? [c.type] : []);
                return types.includes(options.filterByType);
            });
        }
        return [...filtered].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [clients, options.filterKlaviyo, clientScope, user]);
    // Helper: check if a given client id is archived/inactive
    const isClientInactive = useCallback((clientId) => {
        const clientObj = clientList.find((c) => c._id === clientId || c.value === clientId);
        if (!clientObj)
            return false;
        return (clientObj.status === 'inactive' ||
            clientObj.is_archived === true ||
            clientObj.active === false);
    }, [clientList]);
    const [selectedClientId, setSelectedClientId] = useState(() => {
        const urlId = new URLSearchParams(location.search).get('clientId');
        const isExtra = isExtraPage(location.pathname);
        const stateId = location.state?.clientId || location.state?.client_id;
        const globalId = localStorage.getItem('globalSelectedClientId');
        return (isExtra && contextClientId) || urlId || stateId || globalId || contextClientId || '';
    });
    const [selectedGroupAccountId, setSelectedGroupAccountId] = useState(() => {
        const stateGroupId = location.state?.groupId || location.state?.group_id;
        // Only fallback to global group ID if the global client ID matches the client we are selecting.
        // Otherwise, navigating to a new client would erroneously keep the old client's group account selected.
        const urlId = new URLSearchParams(location.search).get('clientId');
        const stateId = location.state?.clientId || location.state?.client_id;
        const globalId = localStorage.getItem('globalSelectedClientId');
        const contextClient = isExtraPage(location.pathname) ? contextClientId : '';
        const initialClientId = contextClient || urlId || stateId || globalId || contextClientId || '';
        if (initialClientId === globalId) {
            const globalGroupId = localStorage.getItem('globalSelectedGroupAccountId');
            return stateGroupId || globalGroupId || '';
        }
        return stateGroupId || '';
    });
    const [selectedId, setSelectedId] = useState(() => {
        const urlId = new URLSearchParams(location.search).get('clientId');
        const isExtra = isExtraPage(location.pathname);
        const clientId = localStorage.getItem('globalSelectedClientId');
        const stateId = location.state?.clientId || location.state?.client_id;
        const stateGroupId = location.state?.groupId || location.state?.group_id;
        const contextClient = isExtra ? contextClientId : '';
        const initialClientId = contextClient || urlId || stateId || clientId || contextClientId || '';
        let groupId = '';
        if (initialClientId === clientId) {
            groupId = localStorage.getItem('globalSelectedGroupAccountId') || '';
        }
        return (isExtra && contextClientId) || urlId || stateGroupId || stateId || groupId || clientId || contextClientId || '';
    });
    // Ensure localStorage is always in sync with our local state (e.g. after navigating via URL)
    useEffect(() => {
        if (!selectedClientId)
            return;
        const globalId = localStorage.getItem('globalSelectedClientId');
        const globalGroupId = localStorage.getItem('globalSelectedGroupAccountId') || '';
        const currentGroupId = selectedGroupAccountId || '';
        if (globalId !== selectedClientId || globalGroupId !== currentGroupId) {
            localStorage.setItem('globalSelectedClientId', selectedClientId);
            if (currentGroupId) {
                localStorage.setItem('globalSelectedGroupAccountId', currentGroupId);
            }
            else {
                localStorage.removeItem('globalSelectedGroupAccountId');
            }
            window.dispatchEvent(new Event('globalClientChange'));
        }
    }, [selectedClientId, selectedGroupAccountId]);
    // Listen for global client changes
    useEffect(() => {
        const handleGlobalChange = () => {
            if (clientScope === 'assigned')
                return;
            const newClientId = localStorage.getItem('globalSelectedClientId');
            const newGroupId = localStorage.getItem('globalSelectedGroupAccountId');
            if (!newClientId)
                return;
            // Check if the globally selected client is valid in our current list
            const isValid = clientList.some((c) => c._id === newClientId ||
                c.value === newClientId ||
                c.group_accounts?.some((g) => g._id === newClientId || g.value === newClientId));
            if (isValid) {
                // ✅ Sync archive mode with the new client's actual status
                const inactive = isClientInactive(newClientId);
                if (inactive !== isArchiveMode) {
                    setIsArchiveMode(inactive);
                }
                if (newClientId !== selectedClientId) {
                    setSelectedClientId(newClientId);
                    setSelectedId(newGroupId || newClientId);
                    setSelectedGroupAccountId(newGroupId || '');
                }
                else if (newGroupId !== selectedGroupAccountId) {
                    setSelectedGroupAccountId(newGroupId || '');
                    setSelectedId(newGroupId || selectedClientId);
                }
            }
            // If not valid, we do nothing and keep the current selection
            // (or let the other useEffect fallback to the first client on mount)
        };
        window.addEventListener('globalClientChange', handleGlobalChange);
        window.addEventListener('storage', handleGlobalChange);
        return () => {
            window.removeEventListener('globalClientChange', handleGlobalChange);
            window.removeEventListener('storage', handleGlobalChange);
        };
    }, [selectedClientId, selectedGroupAccountId, clientList, isArchiveMode, setIsArchiveMode, isClientInactive]);
    useEffect(() => {
        // ✅ Derive status from isArchiveMode if not explicitly provided
        const status = options.status || (isArchiveMode ? 'inactive' : 'active');
        dispatch(fetchActiveClients({ status, moduleName }));
    }, [options.status, moduleName, isArchiveMode]);
    const selectedClient = useMemo(() => clientList.find((c) => c._id === selectedClientId), [clientList, selectedClientId]);
    const clientOptions = useMemo(() => {
        const options = [];
        clientList.forEach((c) => {
            options.push({ value: c._id, label: c.name });
            if (c.group_accounts && Array.isArray(c.group_accounts)) {
                c.group_accounts.forEach((ga) => {
                    options.push({ value: ga._id, label: ga.name });
                });
            }
        });
        return options;
    }, [clientList]);
    const groupAccountOptions = useMemo(() => {
        let groups = selectedClient?.group_accounts || [];
        // if (clientScope === 'assigned' && entityType !== 'client') {
        //     const assignedIds = user?.employee_portfolio?.map((p: any) => String(p._id || p)) || [];
        //     groups = groups.filter((ga: any) => assignedIds.includes(String(ga._id)));
        // }
        if (options.filterKlaviyo) {
            groups = groups.filter((ga) => ga.connected_networks?.klaviyo === true);
        }
        // Meta-only: drop group stores with no active Meta connection (backend annotates
        // each group account with connected_networks).
        if (options.filterMeta) {
            groups = groups.filter((ga) => ga.connected_networks?.meta === true);
        }
        const mappedGroups = groups.map((ga) => ({
            value: ga._id,
            label: ga.name
        }));
        if (selectedClient && groups.length > 0) {
            mappedGroups.unshift({
                value: selectedClient._id,
                label: selectedClient.name
            });
        }
        return [
            { value: "", label: "Select Group" },
            ...mappedGroups
        ];
    }, [selectedClient, options.filterKlaviyo, options.filterMeta, clientScope, user]);
    const currentSelectedGroupAccountLabel = useMemo(() => {
        const activeId = selectedGroupAccountId || selectedClientId;
        if (!activeId)
            return 'Select';
        if (selectedClient && selectedClient._id === activeId) {
            return selectedClient.name;
        }
        const group = selectedClient?.group_accounts?.find((g) => g._id === activeId || g.value === activeId);
        if (group)
            return group.name || group.label;
        const foundInList = clientList.find((c) => c._id === activeId);
        if (foundInList)
            return foundInList.name;
        return 'Select';
    }, [selectedClient, selectedClientId, selectedGroupAccountId, clientList]);
    useEffect(() => {
        const allGroupAccounts = (selectedClient?.group_accounts || []).map((ga) => ({
            _id: ga._id,
            value: ga._id,
            name: ga.name
        }));
        let clientfulllist = [
            ...clientList,
            ...allGroupAccounts,
            ...groupAccountOptions.filter((g) => g.value !== "")
        ];
        let clientToSelect = '';
        const queryParams = new URLSearchParams(location.search);
        const stateId = location.state?.clientId || location.state?.client_id;
        const urlClientId = queryParams.get('clientId') || stateId;
        if (!urlClientId && (selectedClientId || selectedGroupAccountId)) {
            const currentStillValid = clientfulllist.some((c) => c._id === selectedClientId || c.value === selectedClientId ||
                c._id === selectedGroupAccountId || c.value === selectedGroupAccountId);
            if (currentStillValid) {
                return;
            }
        }
        if (urlClientId) {
            const parent = clientList.find((c) => c._id === urlClientId);
            if (parent) {
                clientToSelect = urlClientId;
            }
            else {
                const parentOfGroup = clientList.find((c) => c.group_accounts?.some((ga) => ga._id === urlClientId));
                if (parentOfGroup) {
                    clientToSelect = urlClientId;
                    const urlParentId = parentOfGroup._id;
                    const urlChildId = urlClientId;
                    setSelectedClientId(urlParentId);
                    setSelectedId(urlChildId);
                    setSelectedGroupAccountId(urlChildId);
                    return;
                }
            }
        }
        const isExtra = isExtraPage(location.pathname);
        if (isExtra && !clientToSelect && contextClientId) {
            const contextClient = clientfulllist.find((c) => c._id === contextClientId || c.value === contextClientId);
            if (contextClient) {
                clientToSelect = contextClient._id || contextClient.value;
            }
        }
        // Global selected client priority
        if (!clientToSelect) {
            const globalId = localStorage.getItem('globalSelectedClientId');
            if (globalId) {
                const globalClient = clientfulllist.find((c) => c._id === globalId || c.value === globalId ||
                    c.group_accounts?.some((g) => g._id === globalId || g.value === globalId));
                if (globalClient) {
                    clientToSelect = globalId;
                }
            }
        }
        // For non-extra pages, contextClientId is a fallback after global
        if (!isExtra && !clientToSelect && contextClientId) {
            const contextClient = clientfulllist.find((c) => c._id === contextClientId || c.value === contextClientId);
            if (contextClient) {
                clientToSelect = contextClient._id || contextClient.value;
            }
        }
        if (!clientToSelect && !options.ignoreCache && cache?.clientId) {
            const cachedClient = clientfulllist.find((c) => (c._id === cache.clientId) || (c.value === cache.clientId));
            if (cachedClient) {
                clientToSelect = cachedClient._id || cachedClient.value;
            }
        }
        if (!clientToSelect) {
            const selectedClient = clientfulllist.find((c) => c.selected === true);
            if (selectedClient) {
                clientToSelect = selectedClient._id || selectedClient.value;
            }
        }
        if (!clientToSelect) {
            const first = clientfulllist[0];
            clientToSelect = first?._id || first?.value;
        }
        if (clientToSelect && clientToSelect !== selectedClientId) {
            const globalId = localStorage.getItem('globalSelectedClientId');
            let newParentId = clientToSelect;
            let newChildId = '';
            if (clientToSelect === globalId) {
                newParentId = globalId;
                newChildId = localStorage.getItem('globalSelectedGroupAccountId') || '';
            }
            else if (!options.ignoreCache && cache?.clientId === clientToSelect) {
                newParentId = cache?.parentId || clientToSelect;
                newChildId = cache?.childId || '';
            }
            else {
                const isParent = clientList.some((c) => c._id === clientToSelect || c.value === clientToSelect);
                if (!isParent) {
                    const parentOfGroup = clientList.find((c) => c.group_accounts?.some((ga) => ga._id === clientToSelect || ga.value === clientToSelect));
                    if (parentOfGroup) {
                        newParentId = parentOfGroup._id || parentOfGroup.value;
                        newChildId = clientToSelect;
                    }
                }
            }
            setSelectedClientId(newParentId);
            setSelectedGroupAccountId(newChildId);
            setSelectedId(newChildId || newParentId);
            // Sync globally so that old group IDs don't linger in localStorage
            localStorage.setItem('globalSelectedClientId', newParentId);
            if (newChildId) {
                localStorage.setItem('globalSelectedGroupAccountId', newChildId);
            }
            else {
                localStorage.removeItem('globalSelectedGroupAccountId');
            }
            window.dispatchEvent(new Event('globalClientChange'));
        }
    }, [clientList, cache?.clientId, cache?.parentId, cache?.childId, location.search, contextClientId]);
    const handleClientChange = useCallback((val, ignoreGroupCondition = false) => {
        const id = Array.isArray(val) ? val[0] : val;
        if (id) {
            let parentId = id;
            let groupId = '';
            if (!ignoreGroupCondition) {
                const isParent = clientList.some((c) => c._id === id || c.value === id);
                if (!isParent) {
                    const parentOfGroup = clientList.find((c) => c.group_accounts?.some((ga) => ga._id === id || ga.value === id));
                    if (parentOfGroup) {
                        parentId = parentOfGroup._id || parentOfGroup.value;
                        groupId = id;
                    }
                }
            }
            if (parentId !== selectedClientId || groupId !== selectedGroupAccountId) {
                setSelectedClientId(parentId);
                setSelectedGroupAccountId(groupId);
                setSelectedId(groupId || parentId);
                // ✅ Sync archive mode with the newly selected client's actual status
                const inactive = isClientInactive(parentId);
                if (inactive !== isArchiveMode) {
                    setIsArchiveMode(inactive);
                }
                // Sync globally
                localStorage.setItem('globalSelectedClientId', parentId);
                localStorage.setItem('globalSelectedGroupAccountId', groupId);
                window.dispatchEvent(new Event('globalClientChange'));
                // Clear location.state and query params so it doesn't override on refresh
                const queryParams = new URLSearchParams(location.search);
                const hasQueryParam = queryParams.has('clientId');
                if (hasQueryParam)
                    queryParams.delete('clientId');
                const stateHasClient = location.state && (location.state.clientId || location.state.client_id);
                if (hasQueryParam || stateHasClient) {
                    const newState = location.state ? { ...location.state } : {};
                    delete newState.clientId;
                    delete newState.client_id;
                    const newSearch = queryParams.toString() ? `?${queryParams.toString()}` : '';
                    navigate(location.pathname + newSearch, { state: newState, replace: true });
                }
                options.onClientChange?.();
            }
        }
    }, [selectedClientId, selectedGroupAccountId, clientList, options.onClientChange, location, navigate, isArchiveMode, setIsArchiveMode, isClientInactive]);
    const handleGroupChange = useCallback((val) => {
        const groupId = Array.isArray(val) ? val[0] : val;
        setSelectedGroupAccountId(groupId);
        setSelectedId(groupId || selectedClientId);
        // Sync globally
        localStorage.setItem('globalSelectedGroupAccountId', groupId || '');
        window.dispatchEvent(new Event('globalClientChange'));
        const queryParams = new URLSearchParams(location.search);
        const hasQueryParam = queryParams.has('groupId');
        if (hasQueryParam)
            queryParams.delete('groupId');
        const stateHasGroup = location.state && (location.state.groupId || location.state.group_id);
        if (hasQueryParam || stateHasGroup) {
            const newState = location.state ? { ...location.state } : {};
            delete newState.groupId;
            delete newState.group_id;
            const newSearch = queryParams.toString() ? `?${queryParams.toString()}` : '';
            navigate(location.pathname + newSearch, { state: newState, replace: true });
        }
    }, [selectedClientId, location, navigate]);
    return {
        selectedClientId,
        selectedGroupAccountId,
        selectedId,
        clientList,
        selectedClient,
        clientOptions,
        groupAccountOptions,
        currentSelectedGroupAccountLabel,
        utilityLoading,
        handleClientChange,
        handleGroupChange,
        cache
    };
};
